import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Space, Typography, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import FilterBar from './FilterBar';
import PdfExportsTable from './PdfExportsTable';
import { fetchPdfExports } from './pdfExportsService';
import { PdfExport, PdfExportFilters, PdfExportStatusValue } from './types';

const PAGE_SIZE = 50;

const emptyFilters: PdfExportFilters = {
    statuses: [],
    from: null,
    to: null,
    accountId: null,
    documentId: null,
    trigger: null,
};

/**
 * Reads filters from the URL on mount and writes them back when the user changes them. Makes
 * dashboards shareable via copy-paste of the URL.
 */
const filtersFromSearchParams = (params: URLSearchParams): PdfExportFilters => ({
    statuses: (params.get('status') ?? '')
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean) as PdfExportStatusValue[],
    from: params.get('from') || null,
    to: params.get('to') || null,
    accountId: params.get('accountId') || null,
    documentId: params.get('documentId') || null,
    trigger: params.get('trigger') || null,
});

const filtersToSearchParams = (filters: PdfExportFilters): Record<string, string> => {
    const out: Record<string, string> = {};
    if (filters.statuses.length > 0) out.status = filters.statuses.join(',');
    if (filters.from) out.from = filters.from;
    if (filters.to) out.to = filters.to;
    if (filters.accountId) out.accountId = filters.accountId;
    if (filters.documentId) out.documentId = filters.documentId;
    if (filters.trigger) out.trigger = filters.trigger;
    return out;
};

const PdfExportsDashboard: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

    const [rows, setRows] = useState<PdfExport[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [nextCursor, setNextCursor] = useState<number | null>(null);
    const [loadingMore, setLoadingMore] = useState<boolean>(false);

    const load = useCallback(
        async (cursor: number | null = null, replace = true) => {
            const isInitialLoad = replace;
            if (isInitialLoad) setLoading(true);
            else setLoadingMore(true);
            setError(null);

            try {
                const response = await fetchPdfExports({ ...filters, cursor, limit: PAGE_SIZE });
                setRows((prev) => (replace ? response.items : [...prev, ...response.items]));
                setNextCursor(response.nextCursorQueuedAtTicks);
            } catch (err: any) {
                setError(err?.response?.data?.message || err?.message || 'Unknown error');
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [filters],
    );

    // Reset + reload whenever filters change.
    useEffect(() => {
        load(null, true);
    }, [load]);

    useEffect(() => {
        document.title = 'PDF Exports · Sonat Support';
    }, []);

    const handleFiltersChange = (next: PdfExportFilters) => {
        setSearchParams(filtersToSearchParams(next));
    };

    const handleClear = () => {
        setSearchParams({});
    };

    const handleRefresh = () => {
        load(null, true);
        message.success('Refreshed');
    };

    const handleLoadMore = () => {
        if (nextCursor != null) load(nextCursor, false);
    };

    return (
        <div style={{ padding: 16 }}>
            <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
                <Typography.Title level={3} style={{ margin: 0 }}>
                    PDF exports
                </Typography.Title>
                <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
                    Refresh
                </Button>
            </Space>

            <FilterBar value={filters} onChange={handleFiltersChange} onClear={handleClear} />

            <PdfExportsTable rows={rows} loading={loading} error={error} />

            {nextCursor != null && (
                <div style={{ textAlign: 'center', marginTop: 16 }}>
                    <Button onClick={handleLoadMore} loading={loadingMore}>
                        Load more
                    </Button>
                </div>
            )}
        </div>
    );
};

export default PdfExportsDashboard;
